// Die Orte der Fundstuecke — deterministisch, aus einer **eigenen** Folge.
//
// **Wortgleiche Entsprechung von `backend/farmining/world/gear.py`** — bis auf
// den Hinweistext, und der steht mit Absicht nur hier (wie bei den Kisten).
//
// Ein Fundstueck ist ein Geraet, das draussen liegt: der Verladearm eines
// aufgegebenen Schleppers. Ohne ihn passt kein Wandschrank und kein
// Containerverbund ins Schiff — der Fund **ist** die Freischaltung, es gibt
// keine Ausbaustufe dafuer.
//
// Die eigene Folge ist kein Schmuck. Die Tabellenposition geht in die Saat ein,
// also haette ein sechster Eintrag in `CACHES` entweder die Kette zerrissen
// (mitten hinein: alle folgenden Kisten wandern) oder hinter den Funden gelegen,
// fuer die man ihn braucht. Ein eigenes Salz hat beide Probleme nicht — dieselbe
// Bewegung wie bei Gegnern, Anomalien, Sonden und Kisten.
//
// Diese Datei importiert **kein Phaser** (Gate FG-18).

import { balancing, gearIndexOf, zonesFor } from '../data/balancing';
import type { Gear } from '../data/types';
import { TAU, clamp, jsRound } from '../core/math';
import { hash3, rngFrom } from '../core/rng';
import { hintOf } from '../core/format';
import { eraSeed } from './field';
import type { Point } from './sidequests';

/**
 * Der Ring, in dem dieses Fundstück liegt.
 *
 * Dieselbe Rechnung wie `cacheBand`, nur mit einem **gebrochenen** Schritt aus
 * der Tabelle statt der Position: so lässt sich ein Gerät *zwischen* zwei Kisten
 * legen, ohne deren Ringe anzufassen. `band = 1.4` heißt „zwischen der zweiten
 * und der dritten Kiste" — man stolpert über den Arm, während man der Spur
 * folgt, und hat ihn, bevor der Werkzeugschrank kommt.
 */
export function gearBand(gear: Gear, era: number): [number, number] {
  const zones = zonesFor(era);
  const step = balancing().cache.step;
  const inner = zones[zones.length - 1].from + Math.max(0, gear.band) * step;
  return [inner, inner + step];
}

/**
 * Der Ort eines Fundstücks. **Die Reihenfolge der Ziehungen ist Vertrag.**
 *
 * Schreibt in einen übergebenen Puffer (Gate FG-14).
 */
export function gearPoint(index: number, seed: number, era: number, out: Point): Point {
  const liste = balancing().gear;
  if (index < 0 || index >= liste.length) {
    out.x = 0;
    out.y = 0;
    return out;
  }

  const welt = eraSeed(seed, era);
  const rng = rngFrom(hash3(hash3(index, 0, welt), balancing().gearWorld.salt, welt));

  const angle = rng() * TAU;                                            // 1
  const span = rng();                                                   // 2

  const [inner, outer] = gearBand(liste[index], era);
  // **Dieselbe Streuung wie bei den Kisten**, aus derselben Zahl. Ein eigener
  // Wert dafür wäre ein zweiter Knopf für dieselbe Frage — und die beiden
  // liefen still auseinander, sobald jemand einen davon dreht.
  const spread = balancing().cache.spread;
  const mitte = (1 - spread) * 0.5;
  const radius = inner + (outer - inner) * (mitte + spread * span);
  out.x = jsRound(Math.cos(angle) * radius);
  out.y = jsRound(Math.sin(angle) * radius);
  return out;
}

/** Die Tabellenposition eines Fundstücks — sie geht in die Saat ein. */
export const gearIndex = (id: string): number => gearIndexOf(id);

/** Der Ort eines konkreten Fundstücks. */
export function gearAt(gear: Gear, seed: number, era: number, out: Point): Point {
  return gearPoint(gearIndex(gear.id), seed, era, out);
}

const puffer: Point = { x: 0, y: 0 };

/** Steht das Schiff nah genug an diesem Fundstück? */
export function atGear(x: number, y: number, gear: Gear, seed: number, era: number): boolean {
  gearAt(gear, seed, era, puffer);
  return Math.hypot(puffer.x - x, puffer.y - y) <= balancing().gearWorld.reach;
}

/**
 * Der Hinweis auf ein Fundstück — **aus seinem Ort gerechnet, nie umgekehrt.**
 *
 * Dieselbe Regel und dieselben Bausteine wie beim Kistenhinweis: gerundete
 * Richtung und Entfernung, keine Koordinate. Ein Wegpunkt wäre kein Hinweis,
 * und das Suchen ist der Inhalt.
 */
export function gearHint(gear: Gear, seed: number, era: number): string {
  gearAt(gear, seed, era, puffer);
  return hintOf(puffer.x, puffer.y);
}

// ── Der Besitzstand ──────────────────────────────────────────────────────────

/** Der Spielstand, soweit ein Fundstück ihn liest. */
export interface GearSave {
  era: number;
  gear: Record<string, boolean>;
}

/**
 * Hat der Spieler dieses Fundstück? Ein **leerer** Name heißt „braucht keins".
 *
 * Der leere Fall steht hier und nicht bei jedem Aufrufer — `needsGear` ist bei
 * den meisten Funden leer, und ein `if (c.needsGear && !…)` an vier Stellen wäre
 * viermal die Gelegenheit, die Hälfte zu vergessen. **Wortgleich zu
 * `rules.has_gear`.**
 */
export const hasGear = (save: GearSave, gearId: string): boolean =>
  !gearId || Boolean(save.gear[gearId]);

/** Die Fundstücke, die in dieser Ära überhaupt liegen — Riegel nur nach vorn. */
export const gearFor = (save: GearSave): Gear[] =>
  balancing().gear.filter((gear) => gear.minEra <= save.era);

/**
 * Fundstücke am Ort `(x, y)` aufnehmen.
 *
 * Anders als bei einer Kiste gibt es **kein zweites Mal**: ein Gerät ist geholt
 * oder nicht, also weder Wiederfund noch Frist. Und es liegt ohne
 * Kettenbedingung da — wer zufällig darüber fliegt, nimmt es mit.
 *
 * Die Solo-Fassung von `rules.find_gear`; dass es zwei gibt, ist der Preis
 * dafür, dass das Spiel ohne Backend spielbar bleibt, und dass beide dasselbe
 * tun, sichern dieselben Testfälle auf beiden Seiten.
 */
export function findGear(
  save: GearSave, x: number, y: number, seed: number, out: string[],
): string[] {
  out.length = 0;
  for (const gear of gearFor(save)) {
    if (save.gear[gear.id]) continue;
    if (!atGear(x, y, gear, seed, save.era)) continue;
    save.gear[gear.id] = true;
    out.push(gear.id);
  }
  return out;
}

/**
 * Wie weit ein Fund schon **am Haken mitgefahren** ist — 0 liegt, 1 am Schiff.
 *
 * Beim **Ausfahren** bleibt er liegen: der Arm greift erst zu, wenn er dort ist.
 * Ein Fund, der schon mitwandert, während die Klaue noch unterwegs ist, sieht
 * aus, als zöge ihn etwas anderes. Erst die zweite Hälfte zieht ihn herein.
 *
 * Sie steht hier und nicht in der Szene, damit ein Test sie **ausführen** kann.
 * Der letzte Meter — das Sprite tatsächlich an die Klaue zu setzen — bleibt
 * Szenencode und braucht einen Browser; geprüft ist die Regel davor. Dieselbe
 * ehrliche Grenze wie bei FG-38, und dieselbe Bewegung wie bei `sim/blast.ts`.
 */
export function hookProgress(left: number, duration: number): number {
  if (duration <= 0) return 0;
  const fortschritt = 1 - clamp(left / duration, 0, 1);
  return fortschritt <= 0.5 ? 0 : (fortschritt - 0.5) * 2;
}
